/**
 * Help Center page
 */

import React, {useState} from 'react';
import {LandingLayout} from '../../components/layout/LandingLayout';
import {Container} from '../../components/ui/Container';
import {Section} from '../../components/ui/Section';
import {SectionHeader} from '../../components/ui/SectionHeader';
import {Card} from '../../components/ui/Card';
import {Input} from '../../components/ui/Input';
import {Button} from '../../components/ui/Button';
import {ROUTES} from '../../config/constants';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

// Icon components
const RocketIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const SparklesIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

const LinkIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
  </svg>
);

const CreditCardIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

const WrenchIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const CodeIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
  </svg>
);

const categories = [
  {
    title: 'Getting Started',
    icon: <RocketIcon />,
    articles: [
      {title: 'Creating your first meeting', href: '#'},
      {title: 'Setting up your account', href: '#'},
      {title: 'Understanding the dashboard', href: '#'},
    ],
  },
  {
    title: 'Features',
    icon: <SparklesIcon />,
    articles: [
      {title: 'Recording meetings', href: '#'},
      {title: 'AI-powered summaries', href: '#'},
      {title: 'Speaker diarization', href: '#'},
      {title: 'Action items extraction', href: '#'},
    ],
  },
  {
    title: 'Integrations',
    icon: <LinkIcon />,
    articles: [
      {title: 'Connecting Zoom', href: '#'},
      {title: 'Google Meet integration', href: '#'},
      {title: 'Slack notifications', href: '#'},
    ],
  },
  {
    title: 'Billing & Plans',
    icon: <CreditCardIcon />,
    articles: [
      {title: 'Upgrading your plan', href: '#'},
      {title: 'Understanding billing', href: '#'},
      {title: 'Canceling subscription', href: '#'},
    ],
  },
  {
    title: 'Troubleshooting',
    icon: <WrenchIcon />,
    articles: [
      {title: 'Audio quality issues', href: '#'},
      {title: 'Transcription errors', href: '#'},
      {title: 'Login problems', href: '#'},
    ],
  },
  {
    title: 'API & Developers',
    icon: <CodeIcon />,
    articles: [
      {title: 'API documentation', href: ROUTES.API},
      {title: 'Authentication', href: '#'},
      {title: 'Webhooks', href: '#'},
    ],
  },
];

const popularArticles = [
  {title: 'How to record a meeting', category: 'Getting Started'},
  {title: 'Understanding AI summaries', category: 'Features'},
  {title: 'Setting up integrations', category: 'Integrations'},
  {title: 'Exporting meeting transcripts', category: 'Features'},
];

export function HelpCenterPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const {ref: headerRef, isVisible: headerVisible} = useScrollAnimation();
  const {ref: searchRef, isVisible: searchVisible} = useScrollAnimation();
  const {ref: popularRef, isVisible: popularVisible} = useScrollAnimation();
  const {ref: categoriesRef, isVisible: categoriesVisible} = useScrollAnimation();
  const {ref: ctaRef, isVisible: ctaVisible} = useScrollAnimation();

  return (
    <LandingLayout>
      <Section padding="lg" background="white" className="pt-32">
        <Container>
          <div
            ref={headerRef as React.RefObject<HTMLDivElement>}
            className={`transition-all duration-700 ${
              headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <SectionHeader
              title="Help Center"
              subtitle="Find answers to common questions and learn how to use VoxaPilot"
              centered
              size="lg"
            />
          </div>

          {/* Search */}
          <div
            ref={searchRef as React.RefObject<HTMLDivElement>}
            className={`max-w-2xl mx-auto mb-16 transition-all duration-700 ${
              searchVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{transitionDelay: '200ms'}}
          >
            <div className="relative">
              <Input
                type="text"
                placeholder="Search for help articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pr-12"
              />
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-[#858585] hover:text-gray-600 dark:hover:text-[#CCCCCC]"
                aria-label="Search"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Popular Articles */}
          <div ref={popularRef as React.RefObject<HTMLDivElement>} className="mb-16">
            <h2
              className={`text-2xl font-semibold text-textPrimary mb-6 transition-all duration-700 ${
                popularVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Popular Articles
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {popularArticles.map((article, index) => (
                <div
                  key={article.title}
                  className={`transition-all duration-700 ${
                    popularVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{transitionDelay: `${index * 0.1}s`}}
                >
                  <Card hover className="p-4">
                    <a href="#" className="block">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-textPrimary mb-1">{article.title}</h3>
                          <p className="text-sm text-textSecondary">{article.category}</p>
                        </div>
                        <svg
                          className="w-5 h-5 text-textSecondary"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </a>
                  </Card>
                </div>
              ))}
            </div>
          </div>

          {/* Categories */}
          <div ref={categoriesRef as React.RefObject<HTMLDivElement>} className="mb-16">
            <h2
              className={`text-2xl font-semibold text-textPrimary mb-6 transition-all duration-700 ${
                categoriesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Browse by Category
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((category, index) => {
                const {ref, isVisible} = useScrollAnimation();
                return (
                  <div
                    key={category.title}
                    ref={ref as React.RefObject<HTMLDivElement>}
                    className={`transition-all duration-700 ${
                      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                    style={{transitionDelay: `${index * 0.1}s`}}
                  >
                    <Card hover className="p-6">
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="text-textSecondary dark:text-[#858585] flex-shrink-0">
                            {category.icon}
                          </div>
                          <h3 className="font-semibold text-lg text-textPrimary dark:text-[#CCCCCC]">{category.title}</h3>
                        </div>
                        <ul className="space-y-2">
                          {category.articles.map((article) => (
                            <li key={article.title}>
                              <a
                                href={article.href}
                                className="text-sm text-textSecondary hover:text-accent transition-colors"
                              >
                                {article.title}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Contact Support */}
          <div
            ref={ctaRef as React.RefObject<HTMLDivElement>}
            className={`text-center bg-gray-50 dark:bg-[#252526] rounded-2xl p-12 transition-all duration-700 ${
              ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h2 className="text-2xl font-semibold text-textPrimary dark:text-[#CCCCCC] mb-4">
              Still Need Help?
            </h2>
            <p className="text-textSecondary dark:text-[#858585] mb-8 max-w-2xl mx-auto">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={ROUTES.CONTACT}>
                <Button variant="primary" size="lg">
                  Contact Support
                </Button>
              </a>
              <a href="mailto:support@voxapilot.com">
                <Button variant="outline" size="lg">
                  Email Us
                </Button>
              </a>
            </div>
          </div>
        </Container>
      </Section>
    </LandingLayout>
  );
}

